import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import {
  BookingError,
  cancelBooking,
  confirmBooking,
  fetchBookingPage,
  rescheduleBooking,
  type ConfirmedBooking,
  type ReasonPayload,
} from '../services/interviewBookingApi';
import {
  CancelledConfirmation,
  Confirmation,
  ErrorPanel,
  MeetingDetails,
  NoticePanel,
} from './interviewBooking/BookingPanels';
import { DetailsForm } from './interviewBooking/DetailsForm';
import { SlotPicker } from './interviewBooking/SlotPicker';
import { SuggestTime } from './interviewBooking/SuggestTime';
import { ReasonStep } from './interviewBooking/ReasonStep';
import { CANCEL_REASONS, RESCHEDULE_REASONS } from './interviewBooking/reasons';
import { firstName, fmtLongDay, fmtTime, groupByDay } from './interviewBooking/format';
import finnLogo from '../../../assets/finn-logo.png';

const errText = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : fallback;

/**
 * Analytics on a PUBLIC page, where the usual "recruiter email is the user id" rule does not
 * apply: the visitor is the candidate, they are never identified, and everything on this page is
 * their personal data. So these events carry only the funnel SHAPE — outcome states, counts,
 * durations and the fixed reason codes from `reasons.ts`.
 *
 * Never send: the link token (it is the authorisation — `redactPath` also scrubs it from the path
 * stamp every sink applies), the candidate's typed name or email, or a reason `note`, which is
 * free text the candidate wrote. `note` is reported as the boolean `has_note` instead.
 */
const bookingEvent = (name: string, props: Record<string, string | number | boolean> = {}) =>
  void logAnalyticsEvent(name, props);

/** HTTP status of a booking failure, or 0 for a transport error — a category, never the message. */
const errStatus = (err: unknown) => (err instanceof BookingError ? err.status : 0);

/** How far ahead the candidate booked. A duration, so it is safe to record; rounded to hours. */
const leadTimeHours = (start: string) =>
  Math.max(0, Math.round((new Date(start).getTime() - Date.now()) / 3_600_000));

/**
 * Candidate interview booking — a Calendly-style page.
 *
 * The candidate sees the panel's MERGED availability and never learns who the
 * interviewers are: the proxy returns opaque free slots plus a panel size, with
 * no names and no meeting titles. Confirming creates one real Google Calendar
 * event with a Meet link, and invites the candidate at the address they type in.
 *
 * Public: mounted outside the auth gate at /book/:token, because candidates have
 * no finn.com account. The link token is the only credential.
 */
export default function InterviewBookingView({
  manageMode = false,
}: {
  /** Entered from the confirmation email: frame the page around the EXISTING
   *  booking, and say so plainly when there isn't one. */
  manageMode?: boolean;
}) {
  // Always a real per-candidate token from the URL — there is no default. A
  // guessable fallback token on a public route would be an open booking page.
  const { token: tokenParam } = useParams<{ token?: string }>();
  const token = tokenParam || '';

  // Only the chosen START is state; the slot object is derived from the latest
  // fetch, so a slot that disappears on refetch clears the selection by itself.
  const [selectedStart, setSelectedStart] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [justBooked, setJustBooked] = useState<ConfirmedBooking | null>(null);
  // Set while the candidate is picking a NEW time for an existing booking.
  const [rescheduling, setRescheduling] = useState(false);
  // A move is confirmed in two steps: pick the slot, then say why.
  const [pendingMove, setPendingMove] = useState<string | null>(null);
  const [cancelStep, setCancelStep] = useState(false);
  // Cleared on cancel so the confirmation from this session stops showing.
  const [cancelled, setCancelled] = useState(false);
  const [cancelledReason, setCancelledReason] = useState<string | null>(null);

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['interview-booking', token],
    queryFn: () => fetchBookingPage(token),
    retry: (count, err) => !(err instanceof BookingError && err.status === 404) && count < 2,
  });

  const days = useMemo(() => groupByDay(data?.slots ?? []), [data?.slots]);
  // Derived, not stored: no effect needed to default the day or drop a stale one.
  const activeDay = days.find((d) => d.key === selectedDay) ?? days[0] ?? null;
  const selectedSlot = selectedStart
    ? (data?.slots.find((s) => s.start === selectedStart) ?? null)
    : null;
  const booking = cancelled ? null : (justBooked ?? data?.booking ?? null);
  // Personalised from their own invite; absent for a link issued without a name.
  const greeting = firstName(data?.candidateName);
  // A refusal the candidate cannot act on: no retry, and no "pick a time" prompt.
  const terminalState =
    error instanceof BookingError && (error.status === 403 || error.status === 410);

  /**
   * Top of the funnel: one event per link opened, reporting WHICH state the candidate landed in.
   * Without it a drop-off is unreadable — an expired link and a working page nobody booked from
   * look identical. Fires once the first fetch settles; the ref keeps a refetch or a re-render
   * from counting the same visit twice.
   */
  // Keyed by TOKEN, not a bare boolean: router navigation can swap `:token` without unmounting
  // this view, and a plain flag would then silently swallow the next link's open event.
  const openReported = useRef<string | null>(null);
  useEffect(() => {
    if (isPending || openReported.current === token) return;
    openReported.current = token;
    const state = terminalState
      ? 'unavailable'
      : error || !data
        ? 'load_failed'
        : data.booking
          ? 'already_booked'
          : data.slotError
            ? 'no_slots'
            : 'bookable';
    bookingEvent('booking_page_opened', {
      state,
      manage_mode: manageMode,
      slot_count: data?.slots.length ?? 0,
      status: errStatus(error),
    });
  }, [isPending, terminalState, error, data, manageMode]);

  async function handleConfirm() {
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      setJustBooked(
        await confirmBooking(token, {
          start: selectedSlot.start,
          candidateName,
          candidateEmail,
        })
      );
      bookingEvent('booking_confirmed', { lead_time_hours: leadTimeHours(selectedSlot.start) });
    } catch (err) {
      // 409 (the slot went while they were typing) is the one worth separating: it is the failure
      // the availability grid is supposed to prevent.
      bookingEvent('booking_confirm_failed', { status: errStatus(err) });
      setSubmitError(errText(err, 'Could not book this slot'));
      // 409 = slot taken meanwhile, or the link was already used. Refetch so the
      // grid stops offering a dead slot.
      if (err instanceof BookingError && err.status === 409) {
        setSelectedStart(null);
        await refetch();
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReschedule(start: string, reason: ReasonPayload) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      setJustBooked(await rescheduleBooking(token, start, reason));
      bookingEvent('booking_rescheduled', {
        reason_code: reason.reasonCode,
        has_note: Boolean(reason.note),
        lead_time_hours: leadTimeHours(start),
      });
      setRescheduling(false);
      setPendingMove(null);
      setSelectedStart(null);
      await refetch();
    } catch (err) {
      bookingEvent('booking_reschedule_failed', { status: errStatus(err) });
      setSubmitError(errText(err, 'Could not move the interview'));
      if (err instanceof BookingError && err.status === 409) await refetch();
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCancel(reason: ReasonPayload) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await cancelBooking(token, reason);
      // The reason CODE is a fixed option from `reasons.ts` and is the whole point of tracking a
      // cancellation — "accepted another offer" and "process too long" are different signals for
      // the hiring team. The candidate's free-text note stays behind.
      bookingEvent('booking_cancelled', {
        reason_code: reason.reasonCode,
        has_note: Boolean(reason.note),
      });
      setCancelStep(false);
      setCancelled(true);
      setCancelledReason(reason.note || null);
      setJustBooked(null);
      setRescheduling(false);
      setSelectedStart(null);
      setCandidateName('');
      setCandidateEmail('');
      // Deliberately NOT refetching: the token is now spent, so the reload would
      // 410 and replace this confirmation with an error.
    } catch (err) {
      bookingEvent('booking_cancel_failed', { status: errStatus(err) });
      setSubmitError(errText(err, 'Could not cancel the interview'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto min-h-screen max-w-5xl px-4 py-10">
      <header className="mb-6">
        {/*
          The candidate reaches this page from a bare token link with no FINN
          chrome around it, so the wordmark is the only thing telling them whose
          interview they are booking. Present on every state — including the
          expired and cancelled ones, which are otherwise a naked error message.
        */}
        <img src={finnLogo} alt="FINN" className="mb-6 h-7 w-auto" />
        <h1 className="text-2xl font-semibold text-gray-900">
          {greeting ? `Hi ${greeting}` : manageMode ? 'Your interview' : 'Book your interview'}
        </h1>
        {cancelled || terminalState ? null : (
          <p className="mt-1 text-sm text-gray-500">
            {manageMode
              ? 'Here’s your interview — you can reschedule or cancel below.'
              : 'Choose a time that suits you. You’ll get a confirmation by email.'}
          </p>
        )}
      </header>

      {isPending ? (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-8 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading availability…
        </div>
      ) : cancelled ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white p-5">
          <CancelledConfirmation reasonGiven={cancelledReason} />
        </div>
      ) : terminalState ? (
        // 403 (needs a personal invitation) and 410 (cancelled / expired / closed)
        // are final answers — retrying cannot change them.
        <NoticePanel message={errText(error, 'This link can no longer be used.')} />
      ) : error || !data ? (
        <ErrorPanel
          message={errText(error, 'Could not load this booking link')}
          onRetry={() => void refetch()}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="grid md:grid-cols-[260px_1fr]">
            <MeetingDetails page={data} selectedSlot={selectedSlot} booking={booking} />

            <div className="p-5">
              {booking && cancelStep ? (
                <ReasonStep
                  heading="Cancel this interview?"
                  subline="Let us know why, and we'll pass it on to the hiring team."
                  confirmLabel="Cancel interview"
                  options={CANCEL_REASONS}
                  busy={submitting}
                  error={submitError}
                  onBack={() => {
                    setCancelStep(false);
                    setSubmitError(null);
                  }}
                  onConfirm={(reason) => void handleCancel(reason)}
                />
              ) : booking && pendingMove ? (
                <ReasonStep
                  heading="Move your interview"
                  subline={`New time: ${fmtLongDay(pendingMove)} at ${fmtTime(pendingMove)}`}
                  confirmLabel="Confirm new time"
                  options={RESCHEDULE_REASONS}
                  busy={submitting}
                  error={submitError}
                  onBack={() => {
                    setPendingMove(null);
                    setSubmitError(null);
                  }}
                  onConfirm={(reason) => void handleReschedule(pendingMove, reason)}
                />
              ) : booking && rescheduling ? (
                <SlotPicker
                  days={days}
                  activeDay={activeDay}
                  onSelectDay={setSelectedDay}
                  onSelectSlot={(slot) => setPendingMove(slot.start)}
                  onRefresh={() => void refetch()}
                  refreshing={isFetching || submitting}
                  heading="Pick a new time"
                  onBack={() => {
                    setRescheduling(false);
                    setSubmitError(null);
                  }}
                  error={submitError}
                />
              ) : booking ? (
                <Confirmation
                  booking={booking}
                  token={token}
                  minutes={data.minutes}
                  // Absent on an older proxy ⇒ self-service as before; the flag only ever removes.
                  selfReschedule={data.selfReschedule !== false}
                  copy={data.copy}
                  onReschedule={() => {
                    bookingEvent('booking_reschedule_started', { manage_mode: manageMode });
                    setRescheduling(true);
                    setSubmitError(null);
                  }}
                  onCancel={() => {
                    // Started, not completed: the drop between this and `booking_cancelled` is
                    // how many candidates think better of it at the reason step.
                    bookingEvent('booking_cancel_started', { manage_mode: manageMode });
                    setCancelStep(true);
                    setSubmitError(null);
                  }}
                  busy={submitting}
                  error={submitError}
                />
              ) : manageMode ? (
                <NothingBooked token={token} />
              ) : data.slotError ? (
                <ErrorPanel
                  message={
                    data.slotError === 'calendar not readable'
                      ? 'A panel calendar could not be read, so no times can be offered. The proxy identity needs free/busy access to every interviewer.'
                      : data.slotError
                  }
                  onRetry={() => void refetch()}
                />
              ) : selectedSlot ? (
                <DetailsForm
                  slot={selectedSlot}
                  minutes={data.minutes}
                  candidateName={candidateName}
                  candidateEmail={candidateEmail}
                  onName={setCandidateName}
                  onEmail={setCandidateEmail}
                  submitting={submitting}
                  error={submitError}
                  onBack={() => {
                    setSelectedStart(null);
                    setSubmitError(null);
                  }}
                  onConfirm={() => void handleConfirm()}
                />
              ) : (
                <>
                  <SlotPicker
                    days={days}
                    activeDay={activeDay}
                    onSelectDay={setSelectedDay}
                    onSelectSlot={(slot) => {
                      // The middle of the funnel: picking a time is where "opened the link" turns
                      // into intent, so a gap between this and `booking_confirmed` points at the
                      // details form rather than at availability.
                      bookingEvent('booking_slot_selected', {
                        lead_time_hours: leadTimeHours(slot.start),
                      });
                      setSelectedStart(slot.start);
                    }}
                    onRefresh={() => void refetch()}
                    refreshing={isFetching}
                  />
                  {data.allowTimeSuggestions ? (
                    <SuggestTime token={token} minutes={data.minutes} onEvent={bookingEvent} />
                  ) : null}
                </>
              )}
              {submitError && !booking && !selectedSlot ? (
                <ErrorPanel message={submitError} onRetry={() => void refetch()} />
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Manage page reached with nothing booked — expired link, already cancelled, or
 * the interview was removed. Saying so is better than silently showing a slot
 * picker, which reads as though the booking never existed.
 */
function NothingBooked({ token }: { token: string }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
      <p className="text-sm font-medium text-gray-700">No interview booked for this link</p>
      <p className="mt-1 text-xs text-gray-500">
        It may have been cancelled already. You can pick a new time below.
      </p>
      <a
        href={`/book/${encodeURIComponent(token)}`}
        className="mt-3 inline-block rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white"
      >
        Choose a time
      </a>
    </div>
  );
}
